import React, { useState, useRef, useEffect, useCallback } from 'react';
import { usePdfViewer } from '../hooks/usePdfViewer';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

/**
 * PdfViewerPanel - Desktop right-side fixed panel for viewing PDFs
 *
 * Positioned at App level like ChatSidebar - floats independently
 *
 * Features:
 * - Fixed position on right side (doesn't affect chat scroll)
 * - Draggable left edge to resize width
 * - Min width: 320px, Max width: 60% of screen
 * - Remembers width in localStorage
 * - Native browser PDF rendering (zoom, search, nav built-in)
 * - Slide in/out animation
 */

const MIN_WIDTH = 320;
const DEFAULT_WIDTH = 480;
const STORAGE_KEY = 'pdfViewerPanelWidth';

const PdfViewerPanel = () => {
  const { isOpen, pdfUrl, pdfTitle, close } = usePdfViewer();

  // Get saved width or default
  const getSavedWidth = () => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  };

  const [width, setWidth] = useState(getSavedWidth);
  const [isDragging, setIsDragging] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [isPortrait, setIsPortrait] = useState(false);
  const panelRef = useRef(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  // Detect if full-screen PDF mode needed (touch devices = always, desktop = never)
  useEffect(() => {
    const checkPortrait = () => {
      const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isSmallScreen = window.innerWidth < 768;
      setIsPortrait(isTouch || isSmallScreen);
    };
    checkPortrait();
    window.addEventListener('resize', checkPortrait);
    return () => window.removeEventListener('resize', checkPortrait);
  }, []);

  // Handle slide animation
  useEffect(() => {
    if (isOpen && pdfUrl) {
      setShouldRender(true);
      // Small delay to trigger CSS transition
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsVisible(true);
        });
      });
    } else {
      setIsVisible(false);
      // Wait for animation to complete before unmounting
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, pdfUrl]);

  // Save width to localStorage when changed
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, width.toString());
  }, [width]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        close();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, close]);

  // Drag handlers
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    startXRef.current = e.clientX;
    startWidthRef.current = width;
  }, [width]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;

    const maxWidth = window.innerWidth * 0.6;
    const deltaX = startXRef.current - e.clientX;
    const newWidth = Math.min(maxWidth, Math.max(MIN_WIDTH, startWidthRef.current + deltaX));
    setWidth(newWidth);
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add/remove mouse listeners for dragging
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  if (!shouldRender) return null;

  const panelTransform = isPortrait
    ? (isVisible ? 'translateY(0)' : 'translateY(100%)')
    : (isVisible ? 'translateX(0)' : 'translateX(100%)');

  return (
    <div
      ref={panelRef}
      className={`diagram-editor-panel ${isPortrait ? 'diagram-editor-panel--mobile' : 'diagram-editor-panel--desktop'}`}
      style={{
        ...(!isPortrait && { width: `${width}px` }),
        transform: panelTransform,
        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      {/* Drag Handle - desktop only */}
      {!isPortrait && (
        <div
          className="absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize hover:bg-blue-500 transition-colors group"
          onMouseDown={handleMouseDown}
        >
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-12 -ml-1.5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="flex gap-0.5">
              <ChevronLeft size={12} className="text-blue-500" />
              <ChevronRight size={12} className="text-blue-500" />
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="relative flex items-center justify-center px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <h3 className="font-medium text-gray-900 dark:text-white truncate max-w-[70%] text-center" title={pdfTitle}>
          {pdfTitle}
        </h3>
        <button
          onClick={close}
          className="absolute right-2 p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
          title="Close (Esc)"
        >
          <X size={18} className="text-gray-600 dark:text-gray-400" />
        </button>
      </div>

      {/* PDF Viewer */}
      <div className="flex-1 overflow-hidden bg-gray-100 dark:bg-gray-800" style={{ minHeight: 0 }}>
        {(() => {
          const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
          const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
          if (isSafari || isIOS) {
            return (
              <iframe
                src={`https://mozilla.github.io/pdf.js/web/viewer.html?file=${encodeURIComponent(pdfUrl)}`}
                className="w-full h-full border-0"
                title={pdfTitle || 'PDF Viewer'}
                style={{ minHeight: 0 }}
              />
            );
          }
          return (
            <iframe
              src={pdfUrl}
              className="w-full h-full border-0"
              title={pdfTitle || 'PDF Viewer'}
              style={{ minHeight: 0 }}
            />
          );
        })()}
      </div>
    </div>
  );
};

export default PdfViewerPanel;
